/**
 * useAutoEnablePush — make notifications "on by default" once permission
 * is granted.
 *
 * Behavior contract (matches user requirement):
 *   • Backend default for every category toggle is already TRUE (see
 *     core/notifications.DEFAULT_PREFS). New users start with all
 *     categories enabled in-memory; no row is inserted at signup time.
 *   • The ONLY remaining hurdle was the user having to click "Enable Push
 *     Notifications" in Settings to actually register a Web-Push
 *     subscription. This hook removes that step.
 *
 * Triggers on every authenticated mount:
 *   1. Service Worker is registered (App.js does this on boot).
 *   2. If `Notification.permission === 'granted'` AND there's no active
 *      pushManager subscription, subscribe NOW. The subscription is silently
 *      registered with the backend so pushes start flowing immediately.
 *   3. If permission is `granted` and a subscription already exists locally
 *      but the backend has none (e.g. user logged in on a fresh device that
 *      already had permission from a previous account), the existing sub is
 *      replayed via the same /api/push/subscribe call so the server sees it.
 *
 * Notes:
 *   • Permission can never be requested without a user gesture (browser
 *     security). On first-ever device use, the in-app banner / Settings
 *     button still triggers `Notification.requestPermission()`. Once it's
 *     granted the FIRST time, every subsequent login auto-subscribes.
 *   • Existing manual preferences are NEVER overwritten. We only register a
 *     push subscription; we do NOT touch /api/notification-preferences.
 *   • Idempotent: backend `/api/push/subscribe` is upsert-by-endpoint, so
 *     retries are harmless.
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
} from '../utils/pushNotifications';

export default function useAutoEnablePush() {
  const { user, token } = useAuth();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!user || !token || ranRef.current) return;
    if (!isPushSupported()) return;
    if (getNotificationPermission() !== 'granted') return;

    ranRef.current = true;
    (async () => {
      try {
        // Check the local pushManager first to avoid an unnecessary subscribe
        // round-trip when the device is already subscribed for this user.
        const reg = await navigator.serviceWorker?.ready;
        const existing = await reg?.pushManager?.getSubscription();
        if (existing) {
          // Re-POST to /api/push/subscribe to ensure the backend has the
          // current endpoint + keys (server upserts by endpoint, so this
          // is idempotent and self-healing if the row was lost).
          await subscribeToPush(token).catch(() => {});
          return;
        }
        // No local subscription, but permission is already granted — quietly
        // subscribe now so the user does not have to find Settings.
        await subscribeToPush(token);
      } catch (e) {
        // Silent failure — Settings page still has the manual fallback. We
        // do NOT toast here because this runs on every login and the user
        // never explicitly asked us to do anything.
        console.debug('[autoEnablePush] skipped:', e?.message || e);
      }
    })();
  }, [user, token]);
}
